/* eslint-disable indent */
import { computed, defineComponent, Ref, h, withDirectives, toRef } from "vue";
import { GeneralField, IVoidFieldFactoryProps, FieldDisplayTypes, ArrayField } from "@formily/core";
import {
  useField,
  useFieldSchema,
  RecursionField as _RecursionField,
  FragmentComponent,
  useForm
} from "@formily/vue";
import { observer } from "@formily/reactive-vue";
import { isArr, isBool } from "@formily/shared";
import type { Schema } from "@formily/json-schema";

import type { VNode, Component, PropType } from "vue";
import { ElTable, ElTableColumn, ElPagination, ElLoading } from "element-plus";

import { Space } from "../../space/src";
import { ArrayBase } from "../../array-base/src";
import { stylePrefix } from "../../__builtins__/configs";
import { composeExport, isVueOptions, resolveComponent } from "../../__builtins__/shared";
import { PreviewText } from "@vue-admin-core/formily/preview-text";
import { batch, model } from "@formily/reactive";
import eachTree from "xe-utils/eachTree.js";
import { each, get } from "lodash-es";

import defaultProps from "element-plus/es/components/table/src/table/defaults";

export const arrayTableProps = {
  ...defaultProps,
  loading: {
    type: Boolean
  },
  table: {
    type: Object as PropType<ITable>
  },
  pagination: {
    type: [Boolean, Object] as PropType<boolean | IPagination>,
    default: false
  }
};

type ElColumnProps = typeof ElTableColumn;

const RecursionField = _RecursionField as unknown as Component;

interface ObservableColumnSource {
  field: GeneralField | undefined;
  fieldProps: IVoidFieldFactoryProps<any, any>;
  columnProps: ElColumnProps & { title: string; asterisk: boolean };
  schema: Schema;
  display: FieldDisplayTypes;
  required: boolean;
  name: string;
  children?: ObservableColumnSource[];
}

type ColumnProps = ElColumnProps & {
  key: string | number;
  asterisk: boolean;
  schema: Schema;
  render?: (
    startIndex?: Ref<number>
  ) => (props: { row: Record<string, any>; column: ElColumnProps; $index: number }) => VNode;
  children?: ColumnProps[];
};

// #region createTable
interface createTable {
  (): ITable;
}

export interface ITable {
  /**
   * ElTable 实例
   */
  instance: InstanceType<typeof ElTable> | null;
  onInit(tableRef: InstanceType<typeof ElTable>): void;
  setExpandRow(row: any, expandedRows: any[] | boolean): void;
  /**
   * 展开的列
   */
  expandRows: any[];
  /**
   * 用于多选表格，清空用户的选择
   */
  clearSelection(): void;
  /**
   * 返回当前选中的行
   */
  getSelectionRows(): any;
  /**
   * 用于多选表格，切换某一行的选中状态， 如果使用了第二个参数，则可直接设置这一行选中与否
   * @param row
   * @param selected
   */
  toggleRowSelection(row: any, selected: boolean): void;
  /**
   * 用于多选表格，切换全选和全不选
   */
  toggleAllSelection(): void;
  /**
   * 用于可扩展的表格或树表格，如果某行被扩展，则切换。 使用第二个参数，您可以直接设置该行应该被扩展或折叠。
   * @param row
   * @param expanded
   */
  toggleRowExpansion(row: any, expanded?: boolean | undefined): void;
  /**
   *  展开所有行
   * @param isTree 是否是树表格
   */
  expandAll(isTree: boolean): any[];
  /**
   * 关闭所有展开行
   */
  clearExpand(): void;
  /**
   * 用于单选表格，设定某一行为选中行， 如果调用时不加参数，则会取消目前高亮行的选中状态。
   * @param row
   */
  setCurrentRow(row: any): void;
  /**
   * 用于清空排序条件，数据会恢复成未排序的状态
   */
  clearSort(): void;
  /**
   * 传入由columnKey 组成的数组以清除指定列的过滤条件。 如果没有参数，清除所有过滤器
   * @param columnKeys
   */
  clearFilter(columnKeys?: string[] | undefined): void;
  /**
   * 对 Table 进行重新布局。 当表格可见性变化时，您可能需要调用此方法以获得正确的布局
   */
  doLayout(): void;
  /**
   * 手动排序表格。 参数 prop 属性指定排序列，order 指定排序顺序。
   * @param prop
   * @param<"ascending" | "descending">order
   */
  sort(prop: string, order: "ascending" | "descending"): void;
  /**
   * 滚动到一组特定坐标
   * @param options
   * @param yCoord
   */
  scrollTo(options: number | ScrollToOptions, yCoord?: number | undefined): void;
  /**
   * 设置垂直滚动位置
   * @param top
   */
  setScrollTop(top?: number | undefined): void;
  /**
   * 设置水平滚动位置
   * @param left
   */
  setScrollLeft(left?: number | undefined): void;
}
// #endregion createTable

// #region createPagination
type PaginationOptions = {
  // 当前页 默认1
  current?: number;
  // 页面大小 默认 10
  pageSize?: number;
  // 总是 默认 0
  total?: number;
  // 可选的每页显示条数 默认 [10, 20, 50, 100]
  pageSizes?: number[];
  // 位置 默认 right
  align?: "left" | "right" | "center";
  // 改变事件
  onChange?: (current: number, pageSize: number) => void;
};
interface createPagination {
  (options?: PaginationOptions): IPagination;
}

interface IPagination {
  // 当前页 默认1
  current: number;
  // 页面大小 默认 10
  pageSize: number;
  // 总是 默认 0
  total: number;
  // 可选的每页显示条数 默认 [10, 20, 50, 100]
  pageSizes: number[];
  // 位置 默认 right
  align: "left" | "right" | "center";
  // 改变事件
  onChange?: (current: number, pageSize: number) => void;
  setCurrent(current: number): void;
  setPageSize(pageSize: number): void;
  setPageSizes(pageSizes: number[]): void;
  setTotal(total: number): void;
  setAlign(align: "left" | "right" | "center"): void;
}

// #endregion createPagination
const isColumnComponent = (schema: Schema) => {
  if (typeof schema["x-component"] !== "string") return false;
  return schema["x-component"]?.indexOf("Column") > -1;
};

const isOperationsComponent = (schema: Schema) => {
  if (typeof schema["x-component"] !== "string") return false;
  return schema["x-component"]?.indexOf("Operation") > -1;
};

const isAdditionComponent = (schema: Schema) => {
  if (typeof schema["x-component"] !== "string") return false;
  return schema["x-component"]?.indexOf("Addition") > -1;
};

const getDefaultRender = (schema: Schema) => {
  const content = schema["x-render"];
  if (typeof content === "string") return content;
  if (typeof content === "function") return content;
  if (typeof content === "object" && !isVueOptions(content) && content["default"])
    return content["default"];
  return undefined;
};

/**
 *
 * @param arrayFieldRef
 * @param schemaRef
 * @returns
 */
const getArrayTableSources = (arrayFieldRef: Ref<ArrayField>, schemaRef: Ref<Schema>) => {
  const arrayField = arrayFieldRef.value;
  const parseSources = (schema: Schema): ObservableColumnSource[] => {
    if (isColumnComponent(schema) || isOperationsComponent(schema) || isAdditionComponent(schema)) {
      if (!schema["x-component-props"]?.["prop"] && !schema["name"]) return [];
      const name = schema["x-component-props"]?.["prop"] || schema["name"];
      const field = arrayField.query(arrayField.address.concat(name)).take();
      const fieldProps = field?.props || schema.toFieldProps();
      const columnProps = (field?.component as any[])?.[1] || schema["x-component-props"] || {};
      const display = field?.display || schema["x-display"] || "visible";

      const required = schema.reduceProperties((required, property) => {
        if (required) {
          return required;
        }
        return !!property.required;
      }, false);
      return [
        {
          name,
          display,
          required,
          field,
          fieldProps,
          schema,
          columnProps,
          children: schema["x-table-header"]
            ? schema.reduceProperties((buf: any[], schema) => {
                return buf.concat(parseSources(schema));
              }, [])
            : undefined
        }
      ];
    } else if (schema.properties) {
      return schema.reduceProperties((buf: any[], schema) => {
        return buf.concat(parseSources(schema));
      }, []);
    } else {
      return [];
    }
  };

  const parseArrayTable = (schema: Schema["items"]) => {
    if (!schema) return [];
    const sources: ObservableColumnSource[] = [];
    const items = isArr(schema) ? schema : ([schema] as Schema[]);
    return items.reduce((columns, schema) => {
      const item = parseSources(schema);
      if (item) {
        return columns.concat(item);
      }
      return columns;
    }, sources);
  };

  if (!schemaRef.value) throw new Error("can not found schema object");

  return parseArrayTable(schemaRef.value.items);
};

const getArrayTableColumns = (sources: ObservableColumnSource[]): ColumnProps[] => {
  return sources.reduce(
    (
      buf: ColumnProps[],
      { name, columnProps, schema, display, required, field, children },
      key
    ) => {
      const { title, asterisk, ...props } = columnProps;
      if (display !== "visible") return buf;
      if (!isColumnComponent(schema) && !isOperationsComponent(schema)) return buf;

      if (isOperationsComponent(schema) && field?.pattern !== "editable") return buf;

      const render = (startIndex?: Ref<number>) => {
        return columnProps?.type &&
          columnProps?.type !== "default" &&
          columnProps?.type !== "expand"
          ? undefined
          : (props: { row: Record<string, any>; column: ElColumnProps; $index: number }): VNode => {
              const index = (startIndex?.value ?? 0) + props.$index;
              const children = h(
                ArrayBase.Item,
                { index, record: props.row, key: `${key}${index}` },
                {
                  default: () => {
                    const defaultRender = getDefaultRender(schema);
                    if (defaultRender) {
                      return resolveComponent(defaultRender, props);
                    }
                    if (!schema.properties) {
                      if (columnProps.valueType === "date") {
                        return h(PreviewText.DatePicker, {
                          value: get(props.row, name),
                          format: columnProps.format || "YYYY-MM-DD"
                        });
                      } else if (columnProps.valueType === "dateTime") {
                        return h(PreviewText.DatePicker, {
                          value: get(props.row, name)
                        });
                      } else if (columnProps.valueType === "time") {
                        return h(PreviewText.TimePicker, {
                          value: get(props.row, name),
                          format: columnProps.format
                        });
                      } else if (columnProps.valueType === "currency") {
                        return h(PreviewText, {
                          value: get(props.row, name),
                          format: columnProps.format || "$0,0.00"
                        });
                      } else if (columnProps.valueType === "bytes") {
                        return h(PreviewText, {
                          value: get(props.row, name),
                          format: columnProps.format || "0b"
                        });
                      } else if (columnProps.valueType === "percent") {
                        return h(PreviewText, {
                          value: get(props.row, name),
                          format: columnProps.format || "0%"
                        });
                      } else if (columnProps.valueType === "address") {
                        return h(PreviewText.Cascader, { value: get(props.row, name) });
                      }
                      if (schema.enum) {
                        return h(PreviewText.Select, {
                          value: get(props.row, name),
                          options: schema.enum,
                          multiple: columnProps.multiple
                        });
                      }
                      return h(PreviewText, {
                        value: get(props.row, name),
                        format: columnProps.format
                      });
                    }
                    return h(
                      RecursionField as any,
                      {
                        schema,
                        name: index,
                        onlyRenderProperties: true
                      },
                      {}
                    );
                  }
                }
              );
              return children;
            };
      };

      const item = {
        label: title,
        ...props,
        key,
        prop: name,
        asterisk: asterisk ?? required,
        schema
      };
      if (children && children.length > 0) {
        (item as any).children = getArrayTableColumns(children);
      } else {
        item.render = render;
      }

      return (buf as any).concat(item);
    },
    []
  );
};

const renderAddition = () => {
  const schema = useFieldSchema();
  return schema.value.reduceProperties((addition, schema) => {
    if (isAdditionComponent(schema) || schema["x-addition"]) {
      return h(
        RecursionField as any,
        {
          schema,
          name: "addition"
        },
        {}
      );
    }
    return addition;
  }, null);
};
const createPagination = (options?: PaginationOptions) => {
  const pagination = model<IPagination>({
    current: options?.current || 1,
    pageSize: options?.pageSize || 10,
    pageSizes: options?.pageSizes || [10, 20, 50, 100],
    total: options?.total || 0,
    align: options?.align || "right",
    onChange: options?.onChange,
    setCurrent(current: number) {
      pagination.current = current;
    },
    setPageSize(pageSize: number) {
      pagination.pageSize = pageSize;
    },
    setPageSizes(pageSizes: number[]) {
      pagination.pageSizes = pageSizes;
    },
    setTotal(total: number) {
      pagination.total = total;
    },
    setAlign(align: "left" | "right" | "center") {
      pagination.align = align;
    }
  });

  return pagination;
};

const createTable = () => {
  const table: ITable = {
    instance: null as InstanceType<typeof ElTable> | null,
    expandRows: [] as any[],
    onInit(tableRef: InstanceType<typeof ElTable>) {
      table.instance = tableRef;
    },
    setExpandRow(row: any, expandedRows: any[] | boolean) {
      if (typeof expandedRows === "boolean") {
        if (expandedRows) {
          table.expandRows.push(row);
        } else {
          table.expandRows.splice(table.expandRows.indexOf(row), 1);
        }
      } else {
        table.expandRows = expandedRows;
      }
    },
    clearSelection() {
      table.instance?.clearSelection();
    },
    getSelectionRows() {
      return table.instance?.getSelectionRows();
    },
    toggleRowSelection(row: any, selected: boolean) {
      table.instance?.toggleRowSelection(row, selected);
    },
    toggleAllSelection() {
      table.instance?.toggleAllSelection();
    },
    toggleRowExpansion(row: any, expanded?: boolean | undefined) {
      table.instance?.toggleRowExpansion(row, expanded);
    },
    expandAll(isTree: boolean) {
      const rows: any[] = [];
      if (isTree) {
        const { children, hasChildren } = table.instance?.treeProps as any;
        eachTree(
          table.instance?.data,
          (item: any) => {
            if ((item[children] && item[children].length > 0) || item[hasChildren]) {
              rows.push(item);
              table.toggleRowExpansion(item, true);
            }
          },
          { children }
        );
      } else {
        each(table.instance?.data, (item: any) => {
          rows.push(item);
          table.toggleRowExpansion(item, true);
        });
      }
      table.expandRows = rows;
      return rows;
    },
    clearExpand() {
      each([...table.expandRows], (item: any) => {
        table.toggleRowExpansion(item, false);
      });
      table.expandRows = [];
    },
    setCurrentRow(row: any) {
      table.instance?.setCurrentRow(row);
    },
    clearSort() {
      table.instance?.clearSort();
    },
    clearFilter(columnKeys?: string[] | undefined) {
      table.instance?.clearFilter(columnKeys);
    },
    doLayout() {
      table.instance?.doLayout();
    },
    sort(prop: string, order: string) {
      table.instance?.sort(prop, order);
    },
    scrollTo(options: number | ScrollToOptions, yCoord?: number | undefined) {
      table.instance?.scrollTo(options, yCoord);
    },
    setScrollTop(top?: number | undefined) {
      table.instance?.setScrollTop(top);
    },
    setScrollLeft(left?: number | undefined) {
      table.instance?.setScrollLeft(left);
    }
  };
  return table;
};

const ArrayTablePagination = defineComponent({
  inheritAttrs: false,
  props: ["dataSource", "pagination"],
  setup(props, { attrs, slots }) {
    const prefixCls = `${stylePrefix}-array-table`;

    const dataSource = computed(() => props.dataSource);
    const paginationRef = computed(() => props.pagination ?? createPagination());

    const renderPagination = function () {
      const pagination = paginationRef.value;
      const current = pagination.current;
      const pageSize = pagination.pageSize;
      const pageSizes = pagination.pageSizes;
      const total = props.pagination ? pagination.total : dataSource.value?.length;
      const totalPage = Math.ceil(total / pageSize);
      const align = pagination.align;
      if (totalPage <= 1) return;
      return h(
        "div",
        {
          class: [`${prefixCls}-pagination`],
          style: {
            justifyContent: align
          }
        },
        h(
          Space,
          {},
          {
            default: () => [
              h(
                ElPagination,
                {
                  background: true,
                  layout: "total, sizes, prev, pager, next, jumper",
                  ...attrs,
                  pageSize: pageSize,
                  pageSizes: pageSizes,
                  pageCount: totalPage,
                  currentPage: current,
                  total: total,
                  small: true,
                  onCurrentChange: (val: number) => {
                    pagination.setCurrent(val);
                    // pagination.onChange?.(val, pageSize);
                  },
                  onSizeChange: (val: number) => {
                    batch(() => {
                      pagination.setCurrent(1);
                      pagination.setPageSize(val);
                    });
                    // pagination.onChange?.(current, val);
                  }
                },
                {}
              )
            ]
          }
        )
      );
    };

    return () => {
      const pagination = paginationRef.value;
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize - 1; // computed(() => startIndex.value + pageSize.value - 1);
      return h(
        FragmentComponent,
        {},
        {
          default: () =>
            slots?.default?.(
              props.pagination
                ? dataSource.value
                : dataSource.value?.slice(startIndex, endIndex + 1),
              renderPagination,
              props.pagination ? null : toRef(startIndex)
            )
        }
      );
    };
  }
});

const ArrayTableInner = observer(
  defineComponent({
    name: "FArrayTable",
    inheritAttrs: false,
    props: arrayTableProps,
    setup(props, { attrs, slots }) {
      const fieldRef = useField<ArrayField>();
      const schemaRef = useFieldSchema();
      const formRef = useForm();
      const prefixCls = `${stylePrefix}-array-table`;
      const { getKey, keyMap } = ArrayBase.useKey(schemaRef.value);
      const defaultRowKey = (record: any, index: number) => {
        return getKey(record, index);
      };

      const tableRef = computed(() => props.table ?? createTable());

      return () => {
        // const props = attrs as unknown as ArrayTableProps;
        const field = fieldRef.value;
        const form = formRef.value;
        const dataSource = Array.isArray(field.value) ? field.value.slice() : [];
        const pagination = props.pagination;
        const sources = getArrayTableSources(fieldRef, schemaRef);
        const columns = getArrayTableColumns(sources);

        const renderColumns = (startIndex?: Ref<number>) => {
          const renderColumn = (columns: ColumnProps[]) => {
            return columns.map(
              ({ key, render, asterisk, schema, children: headerChildren, ...props }) => {
                if (headerChildren) {
                  return h(
                    ElTableColumn as any,
                    {
                      ...props,
                      key
                    },
                    {
                      default: () => renderColumn(headerChildren)
                    }
                  );
                }

                const children = {} as Record<string, any>;
                if (render) {
                  children.default = render(startIndex);
                }
                if (asterisk) {
                  children.header = ({
                    column,
                    $index
                  }: {
                    column: ElColumnProps;
                    $index: number;
                  }) =>
                    h("span", {}, [
                      h("span", { class: `${prefixCls}-inner-asterisk` }, "*"),
                      schema["x-render"] && schema["x-render"]["header"]
                        ? resolveComponent(schema["x-render"]["header"], { column, $index })
                        : column.label
                    ]);
                } else {
                  if (schema["x-render"] && schema["x-render"]["header"]) {
                    children.header = ({
                      column,
                      $index
                    }: {
                      column: ElColumnProps;
                      $index: number;
                    }) => resolveComponent(schema["x-render"]["header"], { column, $index });
                  }
                }
                return h(
                  ElTableColumn as any,
                  {
                    ...props,
                    key
                  },
                  children
                );
              }
            );
          };

          return renderColumn(columns);
        };

        const renderStateManager = () =>
          sources.map((column, key) => {
            //专门用来承接对Column的状态管理
            if (!isColumnComponent(column.schema) && !isOperationsComponent(column.schema)) return;
            return h(
              RecursionField as any,
              {
                name: column.name,
                schema: column.schema,
                onlyRenderSelf: true,
                key
              },
              {}
            );
          });
        const renderTable = (dataSource?: any[], pager?: () => VNode, startIndex?: Ref<number>) => {
          return h(
            "div",
            { class: prefixCls },
            h(
              ArrayBase,
              {
                keyMap
              },
              {
                default: () => [
                  withDirectives(
                    h(
                      ElTable as any,
                      {
                        ...props,
                        ...attrs,
                        rowKey: props.rowKey ? props.rowKey : defaultRowKey,
                        data: dataSource,
                        ref: (inst) => {
                          tableRef.value.onInit(inst as any);
                          field.inject({
                            getRef: () => inst
                          });
                        },
                        onExpandChange: (row: any, expandedRows: any[] | boolean) => {
                          tableRef.value.setExpandRow(row, expandedRows);
                          (attrs as any)?.onExpandChange?.(row, expandedRows);
                        }
                      },
                      {
                        ...slots,
                        default: () => renderColumns(startIndex)
                      }
                    ),
                    [
                      [
                        ElLoading.directive,
                        props.loading !== undefined
                          ? props.loading
                          : field?.loading || form?.submitting
                      ]
                    ]
                  ),
                  pager?.(),
                  renderStateManager(),
                  renderAddition()
                ]
              }
            )
          );
        };

        if (!pagination) {
          return renderTable(dataSource);
        }
        return h(
          ArrayTablePagination,
          {
            pagination: isBool(pagination) ? null : pagination,
            dataSource
          },
          { default: renderTable }
        );
      };
    }
  })
);

const ArrayTableColumn: Component = {
  name: "FArrayTableColumn",
  render() {
    return null;
  }
};

export const ArrayTable = composeExport(ArrayTableInner, {
  Column: ArrayTableColumn,
  Index: ArrayBase.Index,
  SortHandle: ArrayBase.SortHandle,
  Addition: ArrayBase.Addition,
  Remove: ArrayBase.Remove,
  MoveDown: ArrayBase.MoveDown,
  MoveUp: ArrayBase.MoveUp,
  useArray: ArrayBase.useArray,
  useIndex: ArrayBase.useIndex,
  useRecord: ArrayBase.useRecord,
  ButtonGroup: ArrayBase.ButtonGroup,
  Button: ArrayBase.Button,
  createPagination: createPagination,
  createTable: createTable
});

export default ArrayTable;
